/**
 * Экран «Готовые программы».
 *
 * Зачем он есть. Движок умеет собирать мезоцикл сам, но человек имеет право
 * взять известную методику целиком — PPL, GZCLP, 5×5, 5/3/1 — и дальше править
 * её под себя правкой тренировок. Экран — это витрина: что за программа, чья
 * она, чего потребует и из чего состоит.
 *
 * Чего здесь принципиально нет. Клиент не считает ничего (раздел 2, принцип 1):
 * ни весов, ни объёма, ни «подойдёт ли тебе». Порядок списка тоже не наш —
 * сервер отдаёт программы от наименее требовательной к самой требовательной,
 * и пересортировка сломала бы этот смысл. Проценты переводятся из доли (0.85)
 * в «85%» — это форматирование уже полученного числа, а не расчёт: сам вес
 * назначит движок из истории движения.
 *
 * Честность (раздел 2, принцип 3) здесь стоит дороже обычного, потому что
 * «Взять эту программу» — необратимое действие: пересборка заменяет текущий
 * мезоцикл. Поэтому предупреждение висит РЯДОМ С КНОПКОЙ до нажатия, а не
 * всплывает после, а состав программы можно посмотреть, ничего не ломая.
 * И ровно поэтому же экран сначала читает текущий мезоцикл: пока плана нет,
 * пугать заменой нечем, а когда он есть — режим и цель по снижению жира
 * переносятся на новый (программа задаёт структуру, а не фазу питания).
 */

import { Fragment, useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { ApiError, api, NetworkError } from '../api/client'
import type {
  Mesocycle,
  ProgramDetail,
  ProgramExercise,
  ProgramSummary,
  RebuildInput,
} from '../api/types'
import { haptic } from '../auth/telegram'
import BodyMap from '../components/BodyMap'
import { ExerciseHead } from '../components/ExerciseHead'
import { hasPeek } from '../components/ExerciseThumb'
import TechniqueCard, { TechniqueCredit } from '../components/TechniqueCard'
import { ConfirmButton, Empty, ErrorBox, Loader } from '../components/ui'
import { useAsync } from '../hooks'

/** Состав программы грузится по нажатию: шесть полных программ на старте — лишняя минута связи в зале. */
type DetailState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; data: ProgramDetail }
  | { status: 'error'; error: unknown }

/**
 * Что у человека собрано прямо сейчас.
 *
 * Нужно по двум причинам, и обе про честность. Первая: пока плана нет,
 * «Заменит текущий план» — прямая ложь, а с экрана «Программа» без мезоцикла
 * ведёт именно сюда. Вторая: режим (`deficit`/`maintenance`/`surplus`) и цель
 * по снижению жира — свойства ПОЛЬЗОВАТЕЛЯ, а не программы. Сервер их из тела
 * пересборки не наследует: не прислал — подставил свои умолчания (`surplus`,
 * цель снята). Тогда взятая программа молча перевела бы человека из дефицита в
 * набор, а это меняет трактовку всех его цифр (раздел 5.6) и убирает кардио
 * (раздел 5.7).
 */
interface PlanSettings {
  /** Активный мезоцикл есть — «Взять» действительно заменит план. */
  hasPlan: boolean
  /** Режим активного мезоцикла. `null` — плана нет, наследовать нечего. */
  mode: string | null
  /** Заявленная при сборке цель по снижению жира. `null` — неизвестна. */
  fatLossGoal: boolean | null
}

/** 404 — «плана ещё нет». Это ответ, а не сбой. Остальное — настоящая ошибка. */
function nullOn404(e: unknown): null {
  if (e instanceof ApiError && e.status === 404) return null
  throw e
}

async function loadSettings(): Promise<PlanSettings> {
  const [meso, cardio] = await Promise.all([
    api.get<Mesocycle>('/mesocycle').catch(nullOn404),
    // Цель по снижению жира сервер хранит на пользователе и отдаёт вместе с
    // планом кардио — в самом мезоцикле её нет.
    api.get<{ fat_loss_goal: boolean }>('/mesocycle/cardio').catch(nullOn404),
  ])
  return {
    hasPlan: meso !== null,
    mode: meso?.mode ?? null,
    fatLossGoal: cardio?.fat_loss_goal ?? null,
  }
}

/** Режим человеческими словами. Незнакомый показываем как есть: выдумывать перевод — врать. */
const MODE_LABELS: Record<string, string> = {
  deficit: 'дефицит',
  maintenance: 'поддержание',
  surplus: 'набор',
}

interface ScreenData {
  list: ProgramSummary[]
  /** `null` — текущее состояние прочитать не удалось; тогда предупреждаем по худшему. */
  settings: PlanSettings | null
}

export default function Programs() {
  const programs = useAsync<ScreenData>(async () => {
    const [list, settings] = await Promise.all([
      api.get<ProgramSummary[]>('/programs'),
      // Каталог — смысл экрана, и ронять его из-за неизвестного режима нельзя:
      // читать программы можно и так, а перед самой пересборкой попробуем ещё раз.
      loadSettings().catch(() => null),
    ])
    return { list, settings }
  }, [])
  const navigate = useNavigate()

  // Какую программу сейчас собирает сервер. Второе нажатие, пока первое в
  // полёте, собрало бы два мезоцикла подряд — второй затёр бы первый.
  const [takingCode, setTakingCode] = useState<string | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const list = programs.data?.list ?? []
  const settings = programs.data?.settings ?? null
  // Настройки не прочитались — считаем, что план есть: обещать «заменять
  // нечего» и стереть мезоцикл хуже, чем предупредить лишний раз.
  const willReplace = settings === null || settings.hasPlan
  const modeNote =
    settings?.mode != null
      ? `Режим останется прежним — ${MODE_LABELS[settings.mode] ?? settings.mode}.`
      : null

  async function take(code: string) {
    if (takingCode !== null) return
    setTakingCode(code)
    setActionError(null)
    try {
      // Режим и цель по кардио переносим с текущего мезоцикла. Если при
      // загрузке экрана прочитать их не вышло — читаем сейчас, ДО пересборки:
      // отказ здесь оставляет план нетронутым, а молча собранный «набор»
      // вместо дефицита — нет.
      const current = settings ?? (await loadSettings())

      // days_per_week и weeks не шлём сознательно: их определяет сама
      // программа, а свои числа здесь означали бы обещать одно, а получить
      // другое (сервер их в этом случае всё равно игнорирует).
      const body: RebuildInput = { program_code: code }
      if (current.mode !== null) body.mode = current.mode
      if (current.fatLossGoal !== null) body.fat_loss_goal = current.fatLossGoal

      await api.post<Mesocycle>('/mesocycle/rebuild', body)
      haptic('heavy')
      // Уходим на план: там уже видно, что именно собралось. Сбрасывать
      // takingCode не нужно — экран уходит вместе с состоянием.
      navigate('/plan')
    } catch (e) {
      setActionError(describe(e))
      setTakingCode(null)
    }
  }

  if (programs.loading && programs.data === null) {
    return (
      <div className="screen">
        <BackLink />
        <h1>Готовые программы</h1>
        <Loader label="Загружаю каталог…" />
      </div>
    )
  }

  if (programs.error && programs.data === null) {
    return (
      <div className="screen">
        <BackLink />
        <h1>Готовые программы</h1>
        <ErrorBox error={programs.error} onRetry={() => void programs.reload()} />
      </div>
    )
  }

  return (
    <div className="screen">
      <BackLink />
      <h1>Готовые программы</h1>
      <p className="muted" style={{ margin: '0 0 6px' }}>
        Известные методики целиком. Структуру задаёт программа, вес по-прежнему считает
        движок из твоей истории.
      </p>
      <p className="faint" style={{ margin: '0 0 16px' }}>
        Список идёт от самой лёгкой по требованиям к самой тяжёлой. Если на движение
        стоит активная жалоба на боль, в план оно не попадёт (раздел 14).
      </p>

      {/* Каталог не обновился, но старый ответ на экране остался. Молчать
          об этом нельзя: человек решит, что видит свежий список. */}
      {programs.error !== null && programs.data !== null && (
        <div className="flag warning">
          Каталог не обновился — на экране данные из прошлого ответа сервера.
        </div>
      )}

      {actionError && <div className="flag critical">{actionError}</div>}

      {list.length === 0 ? (
        // Сюда попадаем только с успешным ответом: отказ разобран выше. Значит
        // сервер отдал пустой список — писать «не загрузились» было бы враньём.
        <>
          <Empty
            title="Каталог программ пуст"
            hint="Сервер ответил, но не прислал ни одной программы. Повтори — если пусто и дальше, мезоцикл всегда можно собрать движком с экрана «Программа»."
          />
          <button className="btn wide" disabled={programs.loading} onClick={() => void programs.reload()}>
            {programs.loading ? 'Обновляю…' : 'Повторить'}
          </button>
        </>
      ) : (
        list.map((program) => (
          <ProgramCard
            key={program.code}
            program={program}
            busy={takingCode === program.code}
            blocked={takingCode !== null && takingCode !== program.code}
            willReplace={willReplace}
            modeNote={modeNote}
            onTake={() => void take(program.code)}
          />
        ))
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Карточка программы                                                          */
/* -------------------------------------------------------------------------- */

function ProgramCard({
  program,
  busy,
  blocked,
  willReplace,
  modeNote,
  onTake,
}: {
  program: ProgramSummary
  /** Эту программу сервер собирает прямо сейчас. */
  busy: boolean
  /** Собирается другая — свои кнопки на время замолкают. */
  blocked: boolean
  /** Мезоцикл уже собран: взять программу — значит потерять текущий план. */
  willReplace: boolean
  /** «Режим останется прежним — дефицит», когда режим известен. */
  modeNote: string | null
  onTake: () => void
}) {
  const [open, setOpen] = useState(false)
  const [detail, setDetail] = useState<DetailState>({ status: 'idle' })
  // Карточка может уйти с экрана (перезагрузка каталога) раньше ответа —
  // тогда состояние трогать уже некому. Флаг поднимается в теле эффекта, а не
  // только в его уборке: StrictMode в разработке монтирует компонент дважды, и
  // без этого после первой уборки карточка навсегда осталась бы «мёртвой» —
  // состав грузился бы вечным спиннером.
  const alive = useRef(true)
  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  async function loadDetail() {
    setDetail({ status: 'loading' })
    try {
      const data = await api.get<ProgramDetail>(`/programs/${program.code}`)
      if (alive.current) setDetail({ status: 'ready', data })
    } catch (e) {
      if (alive.current) setDetail({ status: 'error', error: e })
    }
  }

  function toggle() {
    if (open) {
      setOpen(false)
      return
    }
    setOpen(true)
    // Уже загруженный состав не перезапрашиваем: программы статичны, они
    // едут вместе с деплоем и за одну сессию не меняются.
    if (detail.status !== 'ready' && detail.status !== 'loading') void loadDetail()
  }

  return (
    <div className="card">
      <div className="row between" style={{ alignItems: 'flex-start', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div className="prg-name">{program.name}</div>
          {/* Автор обязателен: методика чужая, и выдавать её за свою нельзя. */}
          <div className="faint">{program.author}</div>
        </div>
        {program.uses_percentages && <span className="pill warn">веса от максимума</span>}
      </div>

      <div className="muted prg-meta">
        {program.level} · {program.goal} · {program.days_per_week}{' '}
        {plural(program.days_per_week, 'день', 'дня', 'дней')}/нед · {program.weeks}{' '}
        {plural(program.weeks, 'неделя', 'недели', 'недель')}
      </div>

      {/* Summary целиком, без обрезки: он короткий и объясняет, на что человек
          подписывается. Многоточие здесь скрывало бы ровно то, ради чего он есть. */}
      <p className="prg-summary">{program.summary}</p>

      {program.uses_percentages && (
        <p className="faint prg-percent-note">
          Вес считается процентом от расчётного максимума. Пока по движению нет истории,
          брать его не из чего — движок так и скажет: сначала нужна калибровка.
        </p>
      )}

      <button
        className="btn wide"
        onClick={toggle}
        aria-expanded={open}
      >
        {open ? 'Свернуть' : 'Посмотреть'}
      </button>

      {open && (
        <div className="prg-detail">
          {detail.status === 'loading' && <Loader label="Загружаю состав…" />}
          {detail.status === 'error' && (
            <ErrorBox error={detail.error} onRetry={() => void loadDetail()} />
          )}
          {detail.status === 'ready' && <Composition detail={detail.data} />}
        </div>
      )}

      {/* Предупреждение стоит ДО нажатия и не прячется во второй тап
          ConfirmButton: потерять несделанный план молча человек не должен.
          Когда плана нет, пугать нечем — и врать про замену тоже нельзя. */}
      {willReplace ? (
        <div className="prg-warn">
          Заменит текущий план: мезоцикл соберётся заново, незавершённые тренировки из
          него пропадут. Записанные подходы останутся в истории.
          {modeNote && ` ${modeNote}`}
        </div>
      ) : (
        <div className="prg-warn">
          Собранного плана сейчас нет — заменять нечего, эта программа станет первой.
        </div>
      )}

      {busy || blocked ? (
        <button className="btn primary wide big" disabled>
          {busy ? 'Собираю программу…' : 'Взять эту программу'}
        </button>
      ) : (
        <ConfirmButton
          className="btn primary wide big"
          confirmLabel={willReplace ? 'Точно? Текущий план заменится' : 'Точно? Собрать эту программу'}
          onConfirm={onTake}
        >
          Взять эту программу
        </ConfirmButton>
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Состав программы                                                            */
/* -------------------------------------------------------------------------- */

function Composition({ detail }: { detail: ProgramDetail }) {
  // Открытый просмотр техники — один на весь состав. Ключ «день-первая строка
  // серии»: у движения, повторённого подряд, просмотр один.
  const [peek, setPeek] = useState<string | null>(null)

  return (
    <>
      {detail.notes.map((note) => (
        <div key={note} className="prg-note">
          {note}
        </div>
      ))}

      <div className="faint prg-total">
        Всего {detail.weekly_sets} {plural(detail.weekly_sets, 'подход', 'подхода', 'подходов')} в
        неделю
      </div>

      {/* Схема — до дней: сначала «на что эта программа», потом «из чего она».
          Чем неделя на схеме отличается от собранного плана (жалобы на боль,
          калибровка), говорит сервер — его пояснение стоит прямо под схемой.
          Без него цвет обещал бы подходы, которых в плане может не оказаться.
          Карта без групп показывает пояснение сама. */}
      {detail.muscle_map && (
        <div className="prg-map">
          <div className="muted prg-map-title">Нагрузка по мышцам за неделю</div>
          <BodyMap map={detail.muscle_map} compact />
          {detail.muscle_map.status === 'ok' &&
            detail.muscle_map.groups.length > 0 &&
            detail.muscle_map.message && (
              <div className="faint" style={{ marginTop: 8 }}>
                {detail.muscle_map.message}
              </div>
            )}
        </div>
      )}

      {detail.days.map((day, dayIndex) => (
        <div key={`${day.label}-${dayIndex}`} className="prg-day">
          <div className="row between">
            <b>{day.label}</b>
            <span className="pill tnum">
              {day.total_sets} {plural(day.total_sets, 'подход', 'подхода', 'подходов')}
            </span>
          </div>
          {day.exercises.map((item, exIndex) => {
            // Одно движение подряд несколько раз (в 5/3/1 это четыре строки
            // жима с разными процентами) — одна серия. Миниатюра стоит у
            // первой строки, а просмотр раскрывается после последней: внутри
            // серии он разорвал бы её, и четыре строки читались бы как два
            // разных упражнения. Это раскладка ответа, а не расчёт.
            let start = exIndex
            while (start > 0 && day.exercises[start - 1]?.exercise.id === item.exercise.id) {
              start -= 1
            }
            const isEnd = day.exercises[exIndex + 1]?.exercise.id !== item.exercise.id
            const key = `${dayIndex}-${start}`
            return (
              // Индекс в ключе обязателен: одно движение встречается в дне
              // несколько раз, и React схлопнул бы строки в одну.
              <Fragment key={`${item.exercise.id}-${exIndex}`}>
                <ExerciseLine
                  item={item}
                  repeat={start !== exIndex}
                  open={peek === key}
                  onToggle={() => setPeek((prev) => (prev === key ? null : key))}
                />
                {isEnd && peek === key && hasPeek(item.exercise) && (
                  <TechniqueCard
                    exerciseId={item.exercise.id}
                    name={item.exercise.name}
                    onClose={() => {
                      // «Закрыть» исчезает вместе с карточкой — фокус
                      // возвращается на миниатюру, которой просмотр открыли:
                      // раскрыта на составе ровно одна.
                      const button = document.querySelector<HTMLButtonElement>(
                        '.prg-day .xp-thumb[aria-expanded="true"]',
                      )
                      setPeek(null)
                      button?.focus({ preventScroll: true })
                    }}
                  />
                )}
              </Fragment>
            )
          })}
          {/* Одна строка на день: день не длиннее семи строк и помещается на
              экран вместе со своими миниатюрами. */}
          <TechniqueCredit
            exerciseIds={day.exercises.map((e) => e.exercise.id)}
            withSources={false}
          />
        </div>
      ))}
    </>
  )
}

function ExerciseLine({
  item,
  repeat,
  open,
  onToggle,
}: {
  item: ProgramExercise
  repeat: boolean
  open: boolean
  onToggle: () => void
}) {
  const remark = remarkOf(item)
  return (
    <div className="prg-ex">
      <ExerciseHead
        exercise={item.exercise}
        repeat={repeat}
        open={open}
        onToggle={onToggle}
        credit={false}
        inlinePeek={false}
        title={
          <>
            <span>{item.exercise.name}</span>
            <span className="prg-scheme tnum">
              {' — '}
              {scheme(item)}
              {/* Доля от максимума приходит числом 0.85; «85%» — это её запись
                  словами человека, а не расчёт. Сам вес считает движок. */}
              {item.percent_of_e1rm !== null && ` · ${Math.round(item.percent_of_e1rm * 100)}%`}
            </span>
          </>
        }
        sub={remark ? <div className="faint">{remark}</div> : undefined}
      />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Мелочи                                                                      */
/* -------------------------------------------------------------------------- */

function BackLink() {
  return (
    <Link to="/plan" className="btn ghost prg-back">
      ← К программе
    </Link>
  )
}

/** «5×3», «3×8–12». Диапазон в одно число, когда границы совпали. */
function scheme(item: ProgramExercise): string {
  const reps =
    item.reps_min === item.reps_max ? `${item.reps_min}` : `${item.reps_min}–${item.reps_max}`
  return `${item.sets}×${reps}`
}

/**
 * Пометка к упражнению.
 *
 * Заметка автора программы важнее нашей формулировки, поэтому она и
 * показывается. Своими словами говорим только когда автор промолчал, а
 * подход на максимум повторений всё равно есть: он решает, двигать вес,
 * и умалчивать о нём нельзя.
 */
function remarkOf(item: ProgramExercise): string {
  if (item.note) return item.note
  if (!item.amrap) return ''
  return item.sets > 1 ? 'последний подход на максимум' : 'на максимум повторений'
}

/** Русский счётный род: 1 день, 2 дня, 5 дней. */
function plural(n: number, one: string, few: string, many: string): string {
  const mod100 = Math.abs(n) % 100
  if (mod100 >= 11 && mod100 <= 14) return many
  const mod10 = mod100 % 10
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return few
  return many
}

/**
 * Текст ошибки пересборки.
 *
 * Разница между «нет связи» и «сервер отказал» здесь принципиальна: пересборка
 * в офлайн-очередь не попадает (в отличие от подхода), и при обрыве связи план
 * просто остался прежним. Человек должен понимать, что ничего не потерял.
 */
function describe(e: unknown): string {
  if (e instanceof NetworkError) return 'Нет связи — программа не взята, план остался прежним.'
  if (e instanceof Error && e.message) return e.message
  return 'Не удалось собрать программу'
}
